import { round } from 'lodash';
import { ConsiderationType } from 'further-types/exit';
import type { UseFormSetValue } from 'react-hook-form';
import type { ExitDetailsFormValues } from './index';

type CalculateShareConsiderationValuesParams = {
  changedField: 'perShare' | 'total' | 'shares';
  newValue?: string;
  considerationType: ConsiderationType | undefined;
  totalConsiderationShares: string;
  shareConsiderationValuePerShare: string;
  shareConsiderationValueTotal: string;
  isCalculating: React.MutableRefObject<boolean>;
  setValue: UseFormSetValue<ExitDetailsFormValues>;
};

const calculateShareConsiderationValues = ({
  changedField,
  newValue,
  considerationType,
  totalConsiderationShares,
  shareConsiderationValuePerShare,
  shareConsiderationValueTotal,
  isCalculating,
  setValue,
}: CalculateShareConsiderationValuesParams) => {
  if (isCalculating.current) {
    return;
  }

  if (considerationType !== ConsiderationType.Share) {
    return;
  }

  // Use the new value if provided, otherwise read from watched values
  const shares =
    changedField === 'shares'
      ? Number(newValue) || 0
      : Number(totalConsiderationShares) || 0;
  const perShare =
    changedField === 'perShare'
      ? Number(newValue) || 0
      : Number(shareConsiderationValuePerShare) || 0;
  const total =
    changedField === 'total'
      ? Number(newValue) || 0
      : Number(shareConsiderationValueTotal) || 0;

  // Check if the changed field value is valid (not undefined)
  const changedValue = Number(newValue);
  if (isNaN(changedValue)) {
    // Don't calculate if the changed field is undefined/invalid
    return;
  }

  // Don't calculate if shares changes to 0 or negative (invalid state)
  if (changedField === 'shares' && changedValue <= 0) {
    return;
  }

  const hasShares = shares > 0;
  const hasPerShare = perShare > 0;
  const hasTotal = total > 0;

  // Priority 1: If shares exists and total or perShare is changed, calculate the other
  // (shares should stay constant when total or perShare changes)
  if (hasShares) {
    if (changedField === 'total' && shares > 0) {
      // Calculate perShare: total / shares
      // This works whether total is 0 or a valid number
      isCalculating.current = true;
      try {
        if (total === 0) {
          // If total is set to 0, clear perShare
          setValue('shareConsiderationValuePerShare', '0');
        } else {
          const calculatedPerShare = round(total / shares, 10);
          setValue(
            'shareConsiderationValuePerShare',
            calculatedPerShare.toString(),
          );
        }
      } finally {
        isCalculating.current = false;
      }
      return;
    } else if (changedField === 'perShare' && shares > 0) {
      // Calculate total: shares * perShare
      // This works whether perShare is 0 or a valid number
      isCalculating.current = true;
      try {
        if (perShare === 0) {
          // If perShare is set to 0, clear total
          setValue('shareConsiderationValueTotal', '0');
        } else {
          const calculatedTotal = shares * perShare;
          let adjustedTotal = calculatedTotal;
          if (calculatedTotal > 0 && calculatedTotal < 0.01) {
            adjustedTotal = 0.01;
          }
          const roundedTotal = round(adjustedTotal, 2);
          setValue('shareConsiderationValueTotal', roundedTotal.toString());
        }
      } finally {
        isCalculating.current = false;
      }
      return;
    } else if (changedField === 'shares' && hasShares) {
      // When shares changes, calculate based on what exists
      isCalculating.current = true;
      try {
        // If perShare exists and is valid, calculate total
        if (hasPerShare && perShare > 0) {
          const calculatedTotal = shares * perShare;
          let adjustedTotal = calculatedTotal;
          if (calculatedTotal > 0 && calculatedTotal < 0.01) {
            adjustedTotal = 0.01;
          }
          const roundedTotal = round(adjustedTotal, 2);
          setValue('shareConsiderationValueTotal', roundedTotal.toString());
        }
        // If total exists (and perShare doesn't) and is valid, calculate perShare
        else if (hasTotal && total > 0) {
          const calculatedPerShare = round(total / shares, 10);
          setValue(
            'shareConsiderationValuePerShare',
            calculatedPerShare.toString(),
          );
        }
      } finally {
        isCalculating.current = false;
      }
      return;
    }
  }

  // Priority 2: If shares doesn't exist, only calculate when exactly 2 fields are populated
  const populatedCount = [hasShares, hasPerShare, hasTotal].filter(
    Boolean,
  ).length;

  // Only calculate if exactly 2 fields are populated
  // If only 1 field is populated, don't calculate anything
  if (populatedCount !== 2) {
    return;
  }

  isCalculating.current = true;

  try {
    // Calculate the missing field based on which 2 are populated
    // Ensure all values used in calculations are > 0
    if (hasShares && hasPerShare && !hasTotal && shares > 0 && perShare > 0) {
      // Calculate total: shares * perShare
      const calculatedTotal = shares * perShare;
      // Ensure minimum of 0.01 if the value is not 0
      let adjustedTotal = calculatedTotal;
      if (calculatedTotal > 0 && calculatedTotal < 0.01) {
        adjustedTotal = 0.01;
      }
      // Round total to 2 decimal places
      const roundedTotal = round(adjustedTotal, 2);
      setValue('shareConsiderationValueTotal', roundedTotal.toString());
    } else if (
      hasShares &&
      hasTotal &&
      !hasPerShare &&
      shares > 0 &&
      total > 0
    ) {
      // Calculate perShare: total / shares
      const calculatedPerShare = round(total / shares, 10);
      setValue(
        'shareConsiderationValuePerShare',
        calculatedPerShare.toString(),
      );
    } else if (
      hasPerShare &&
      hasTotal &&
      !hasShares &&
      perShare > 0 &&
      total > 0
    ) {
      // Calculate shares: total / perShare
      const calculatedShares = round(total / perShare, 10);
      // Round to whole number (shares should be integers)
      const roundedShares = Math.round(calculatedShares);
      setValue('totalConsiderationShares', roundedShares.toString());
    }
  } finally {
    isCalculating.current = false;
  }
};

export default calculateShareConsiderationValues;
